// Print / save-as-PDF button for the planning tools.
// ใช้ไปป์ไลน์การพิมพ์ของเบราว์เซอร์ (ไม่เพิ่ม dependency, ไม่กินขนาด bundle)
// ร่วมกับกฎ `@media print` ใน src/styles.css
//
// ชื่อไฟล์ PDF: เบราว์เซอร์ทั้ง Chrome/Android และ Safari/iOS ใช้ document.title
// เป็นชื่อไฟล์เริ่มต้น จึงสลับ title ชั่วคราวระหว่างพิมพ์ แล้วคืนค่าเดิมหลังเสร็จ
//
// รองรับการเลือกโหมดภาษาของชื่อไฟล์ (ไทย/อังกฤษ) โดย sanitize ด้วยกฎเดียวกัน
import { useState } from "react";
import { Printer } from "lucide-react";

interface Props {
  label?: string;
  className?: string;
  /** ชื่อไฟล์อัตโนมัติภาษาไทย (ไม่ต้องใส่ .pdf) */
  fileName?: string;
  /** ชื่อไฟล์อัตโนมัติภาษาอังกฤษ — ถ้าไม่ระบุจะซ่อนตัวเลือกภาษา */
  fileNameEn?: string;
}

export function sanitizeFileName(name: string) {
  return name
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 120);
}

export default function PrintResultButton({
  label = "ดาวน์โหลดผลลัพธ์เป็น PDF",
  className,
  fileName,
  fileNameEn,
}: Props) {
  const [lang, setLang] = useState<"th" | "en">("th");
  const chosen = lang === "en" ? (fileNameEn ?? fileName) : fileName;

  const handlePrint = () => {
    if (typeof window === "undefined") return;
    const original = document.title;
    if (chosen) document.title = sanitizeFileName(chosen);

    const restore = () => {
      document.title = original;
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);

    try {
      window.print();
    } finally {
      // Safari/iOS ไม่ยิง afterprint อย่างสม่ำเสมอ — กันไว้ด้วย timeout
      window.setTimeout(restore, 1500);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-print-hide>
      <button
        type="button"
        onClick={handlePrint}
        className={`inline-flex items-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm font-semibold transition hover:bg-muted ${className ?? ""}`}
        data-print-hide
        data-track-zone="tool-print"
      >
        <Printer className="h-4 w-4" aria-hidden /> {label}
      </button>

      {fileNameEn && (
        <div
          className="inline-flex items-center overflow-hidden rounded-lg border border-border text-xs"
          role="group"
          aria-label="ภาษาของชื่อไฟล์ PDF / PDF file name language"
        >
          {(["th", "en"] as const).map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => setLang(l)}
              aria-pressed={lang === l}
              className={`px-2.5 py-1.5 font-semibold transition ${
                lang === l ? "bg-primary text-primary-foreground" : "bg-background hover:bg-muted"
              }`}
            >
              {l === "th" ? "ชื่อไฟล์ ไทย" : "EN file name"}
            </button>
          ))}
        </div>
      )}

      {chosen && (
        <span className="text-[11px] text-muted-foreground">{sanitizeFileName(chosen)}.pdf</span>
      )}
    </div>
  );
}
